import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Bell, CheckCheck } from 'lucide-react'
import { api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { formatRelative } from '@/internationalization/format'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { Button, Drawer, EmptyState, IconButton, SkeletonList } from '@/components/ui'
import { useDisclosure } from '@/hooks'
import type { NotificationItem } from '@/types'
import { RECORDING_PARTIAL_CODES, recordingFailureCode, recordingFailureMessage } from './notificationFailure'
import { notificationHref } from './notificationHref'
import { summaryReadyBody } from './notificationSummaryReady'
import { notificationTitle } from './notificationTitle'

const KIND_TONE: Record<NotificationItem['kind'], string> = {
  summary_ready: 'bg-success',
  action_assigned: 'bg-brand',
  mention: 'bg-accent',
  recording_failed: 'bg-danger',
  system: 'bg-fg-subtle',
}

/**
 * Notification inbox in a drawer rather than a dropdown: items are two lines
 * with a link each, and a 320px dropdown truncates every one of them.
 *
 * The unread count is capped at "9+" so a neglected inbox cannot stretch the
 * top bar.
 */
export function NotificationBell() {
  const t = useT()
  const { isOpen, open, close } = useDisclosure()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: qk.notifications,
    queryFn: () => api.get<NotificationItem[]>('/notifications'),
    refetchInterval: 60_000,
  })

  const unread = data?.filter((n) => !n.read).length ?? 0

  const markAllRead = async () => {
    await api.post('/notifications/read-all')
    queryClient.invalidateQueries({ queryKey: qk.notifications })
  }

  const notificationBody = (item: NotificationItem): string => {
    if (item.kind === 'recording_failed') return recordingFailureMessage(item.body, t)
    if (item.kind === 'summary_ready') return summaryReadyBody(t)
    return item.body
  }

  const notificationTone = (item: NotificationItem): string => {
    if (item.kind === 'recording_failed' && RECORDING_PARTIAL_CODES.has(recordingFailureCode(item.body))) {
      return 'bg-warning'
    }
    return KIND_TONE[item.kind]
  }

  // `meeting_id` travels on the notification itself (migration 004), so
  // `notificationHref` already resolves straight to the meeting's Actions
  // tab -- the `<Link>`'s `to` is the real destination, not a fallback, and
  // there is nothing left to intercept on click beyond marking it read.
  const openNotification = (item: NotificationItem) => {
    if (!item.read) {
      void api
        .post(`/notifications/${encodeURIComponent(item.id)}/read`)
        .finally(() => queryClient.invalidateQueries({ queryKey: qk.notifications }))
    }
    close()
  }

  return (
    <>
      <span className="relative inline-flex">
        <IconButton
          label={unread > 0 ? t('notifications.withUnread', { count: unread }) : t('notifications.title')}
          icon={<Bell />}
          onClick={open}
        />
        {unread > 0 && (
          <span
            aria-hidden
            className="pointer-events-none absolute right-1 top-1 flex h-4 min-w-4 items-center
              justify-center rounded-full bg-danger px-1 text-2xs font-bold text-white ring-2 ring-canvas"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </span>

      <Drawer isOpen={isOpen} onClose={close} title={t('notifications.title')} width="md">
        {isLoading ? (
          <SkeletonList rows={5} className="p-4" />
        ) : !data || data.length === 0 ? (
          <EmptyState
            icon={<Bell />}
            title={t('notifications.caughtUpTitle')}
            description={t('notifications.caughtUpBody')}
          />
        ) : (
          <>
            <div className="flex justify-end border-b border-border px-3 py-2">
              <Button size="sm" variant="ghost" leftIcon={<CheckCheck className="h-4 w-4" />} onClick={markAllRead}>
                {t('notifications.markAllRead')}
              </Button>
            </div>
            <ul>
              {data.map((item) => (
                <li key={item.id} className="border-b border-border last:border-0">
                  <Link
                    to={notificationHref(item)}
                    onClick={() => openNotification(item)}
                    className={cn(
                      'flex gap-3 px-4 py-3 transition-colors hover:bg-surface-muted',
                      !item.read && 'bg-brand-subtle/40',
                    )}
                  >
                    <span aria-hidden className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', notificationTone(item))} />
                    <span className="min-w-0">
                      <span className="block text-base font-medium text-fg">
                        {notificationTitle(item, t)}
                      </span>
                      <span className="mt-0.5 block text-sm text-fg-muted">{notificationBody(item)}</span>
                      <span className="mt-1 block text-xs text-fg-subtle">{formatRelative(item.createdAt)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </Drawer>
    </>
  )
}
